import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Mic, MicOff, Video, VideoOff, RefreshCw, Radio, ChevronLeft, Loader2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import GreenScreenStudio from "../components/GreenScreenStudio";

/**
 * LiveWaitingRoom — dedicated pre-show room for guests invited to a live
 * broadcast on Tap In TV. Replaces the old behavior where invitees were
 * dropped straight onto the public stream as both viewer + pending guest.
 *
 * Flow:
 *   1. Invitee scans QR / clicks ?join=CODE link
 *   2. We POST /api/live/{id}/guest/join (creates a `requested` row).
 *   3. We open the user's camera + mic LOCALLY so they can preview
 *      themselves before going on-air. This NEVER auto-publishes —
 *      the stream is local-only until the host approves.
 *   4. We poll /api/live/{id}/guest/me every 4s to see when the host
 *      flips status to `approved` or `on_air`. On `on_air` we redirect
 *      to /live/{id}?guest=1 where the LiveBroadcaster guest mode picks
 *      up the existing local stream.
 *   5. We PATCH /api/live/{id}/guest/ready with `{camera_ready, mic_ready}`
 *      so the host's GuestManager can show "✓ Camera ready" badges.
 *
 * Guest UX:
 *   - Camera flip button (mobile front/back) via facingMode toggle
 *   - Mic mute / unmute (still applies once on-air)
 *   - Camera off / on (stays off when promoted unless re-enabled)
 *   - Live mic-level meter so the guest can tell if mic is picking them up
 *   - Clear "Waiting for host…" status with spinner
 */
export default function LiveWaitingRoom() {
  const { streamId } = useParams();
  const [search] = useSearchParams();
  const inviteCode = search.get("invite") || search.get("join") || "";
  const { user } = useAuth();
  const nav = useNavigate();

  const [stream, setStream] = useState(null);
  const [status, setStatus] = useState("connecting"); // connecting | requested | waiting | approved | on_air | denied | error
  const [error, setError] = useState("");
  const [streamMeta, setStreamMeta] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [hasMultipleCams, setHasMultipleCams] = useState(false);
  const [level, setLevel] = useState(0);
  // Director-pushed messages + countdown — populated from /guest/me poll.
  const [hostMessage, setHostMessage] = useState(null);
  const [countdown, setCountdown] = useState(null); // { ends_at, message, started_at }
  const [countdownLeft, setCountdownLeft] = useState(0);
  const videoRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(0);

  // Green-screen preview state — gated to admin + premium guests. When
  // active, we run the shared useFxCompositor on the local preview and
  // persist the setting so a returning guest keeps their picked
  // background. The composited track isn't wired to peers from the
  // waiting room (no outbound PC yet), but the `stream` state is swapped
  // so the visible preview reflects what they'll look like on-air.
  const canUseGreenScreen = !!user && ["admin", "premium_creator"].includes(user.role);
  // iter-182 rewrite: let the self-contained `GreenScreenStudio`
  // component own the compositor + UI. The waiting-room simply swaps
  // the preview's visible track between the composite and the raw
  // camera based on what the child publishes.
  const [compositedTrack, setCompositedTrack] = useState(null);

  // Swap what the preview <video> shows depending on whether the
  // compositor produced a track. Raw stream for the camera feed's audio
  // is always included so the mic-level bar keeps working.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // The source `stream` (from getUserMedia) is the immutable raw
    // feed — we never mutate it. Preview srcObject is either set to
    // that raw stream (compositor off) or a fresh composite stream
    // (compositor on). This effect only runs on genuine transitions
    // (stream identity or compositedTrack identity), not on every
    // render — so the <video> element isn't re-initialised when
    // unrelated parent state updates fire.
    if (!stream) return;
    if (compositedTrack) {
      const audioTrack = stream.getAudioTracks?.()[0];
      try {
        el.srcObject = new MediaStream(audioTrack ? [compositedTrack, audioTrack] : [compositedTrack]);
      } catch { el.srcObject = stream; }
    } else {
      el.srcObject = stream;
    }
  }, [compositedTrack, stream]);

  // Open the local camera + mic. The result NEVER goes to a peer connection
  // here — it's purely a self-preview so the guest can fix lighting / mic
  // problems before the host promotes them.
  const openCamera = async (mode = facingMode) => {
    try {
      // If we already have a stream, stop it first to avoid leaking
      // the front camera while we ask for the back one.
      if (stream) {
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      }
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode } },
        audio: true,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;          // never echo guest's own voice
        videoRef.current.playsInline = true;
        try { await videoRef.current.play(); } catch { /* autoplay blocked is fine */ }
      }
      // Detect multi-camera so we can hide the flip button on desktops
      // (most laptops have one webcam — flip button is misleading there).
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = devs.filter((d) => d.kind === "videoinput");
        setHasMultipleCams(cams.length > 1);
      } catch { /* permissions blocked enumerate — leave default */ }
      // Fire ready PATCH so the host sees a green "Camera ready" badge.
      api.post(`/live/${streamId}/guest/ready`, {
        camera_ready: true,
        mic_ready: true,
      }).catch(() => { /* not critical */ });
      // Spin up an analyser node so we can render a live mic-level bar.
      attachLevelMeter(s);
    } catch (e) {
      const msg = (e && e.name === "NotAllowedError")
        ? "Camera + mic permission denied. Refresh the page and tap Allow."
        : (e && e.name === "NotFoundError")
          ? "No camera detected. Plug one in or use a phone."
          : `Camera error: ${e?.message || e}`;
      setError(msg);
      setStatus("error");
    }
  };

  const attachLevelMeter = (s) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(s);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        // RMS-ish level from the time-domain buffer; cheap + good enough.
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 2.5));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { /* no big deal — bar just stays at 0 */ }
  };

  const flipCamera = async () => {
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    await openCamera(next);
  };

  const toggleMic = () => {
    if (!stream) return;
    const enabled = !micOn;
    stream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
    setMicOn(enabled);
    api.post(`/live/${streamId}/guest/ready`, {
      camera_ready: camOn, mic_ready: enabled,
    }).catch(() => {});
  };

  const toggleCam = () => {
    if (!stream) return;
    const enabled = !camOn;
    stream.getVideoTracks().forEach((t) => { t.enabled = enabled; });
    setCamOn(enabled);
    api.post(`/live/${streamId}/guest/ready`, {
      camera_ready: enabled, mic_ready: micOn,
    }).catch(() => {});
  };

  // Bootstrap — fetch the live stream so we can show host name + title,
  // POST /guest/join to create the requested-row, then open the camera.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) {
        nav(`/signup?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      try {
        const { data: meta } = await api.get(`/live/${streamId}`);
        if (cancelled) return;
        setStreamMeta(meta);
        if (!meta?.is_live) {
          setStatus("error");
          setError("This stream isn't live anymore.");
          return;
        }
        // Create / refresh the guest row (idempotent server-side via invite_code).
        try {
          if (inviteCode) {
            await api.post(`/live/${streamId}/guest/join?invite_code=${encodeURIComponent(inviteCode)}`);
          } else {
            await api.post(`/live/${streamId}/guest/join`);
          }
        } catch (e) {
          // 409 = already a guest, fine; other errors surface
          if (e?.response?.status !== 409) throw e;
        }
        if (cancelled) return;
        setStatus("requested");
        await openCamera("user");
      } catch (e) {
        if (cancelled) return;
        setError(formatApiError(e));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      if (audioCtxRef.current) try { audioCtxRef.current.close(); } catch { /* ignore */ }
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  // Stop the local stream when leaving the page so the camera light goes
  // off — otherwise some browsers leave it on for tens of seconds.
  useEffect(() => () => {
    if (stream) stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
  }, [stream]);

  // ----------------------------------------------------------------
  // Guest publish — WebRTC pipeline that pumps the guest's camera up to
  // the host's LiveBroadcaster. The host accepts our `guest_publish_request`
  // automatically (they invited us), and from there it's a vanilla
  // offer/answer/ICE handshake routed by /api/ws/stream signaling.
  //
  // Activates the moment `status` flips to `approved` — that's when the
  // host clicks "Bring on-air" in their GuestManager, so it's the right
  // signal to start pushing video. We stay on the waiting-room page (the
  // host's broadcast composite is what viewers see, not our local view)
  // and our camera tile becomes whatever cam slot the host promotes us to.
  // ----------------------------------------------------------------
  const publishWsRef = useRef(null);
  const publishPcRef = useRef(null);
  const sentOfferRef = useRef(false);
  const [publishState, setPublishState] = useState("idle"); // idle | connecting | publishing | error
  useEffect(() => {
    if (status !== "approved" && status !== "on_air") return undefined;
    if (!stream || !streamId) return undefined;
    if (publishWsRef.current) return undefined; // already running
    let cancelled = false;
    sentOfferRef.current = false;
    setPublishState("connecting");

    (async () => {
      try {
        const tokenStr = localStorage.getItem("tv_token") || "";
        const wsBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, "ws");
        const ws = new WebSocket(`${wsBase}/api/ws/stream/${streamId}?role=viewer&token=${encodeURIComponent(tokenStr)}`);
        publishWsRef.current = ws;
        let broadcasterId = null;
        let iceServers = [{ urls: ["stun:stun.l.google.com:19302"] }];

        const sendOffer = async () => {
          if (!broadcasterId || sentOfferRef.current) return;
          sentOfferRef.current = true;
          const pc = new RTCPeerConnection({ iceServers });
          publishPcRef.current = pc;
          // Prefer the green-screen composited video when the user has
          // enabled it — that's what the preview is showing, so it's
          // what the host should receive. Audio always comes from the
          // raw mic track (the compositor only owns video).
          const videoTrack = compositedTrack || stream.getVideoTracks?.()[0];
          const audioTrack = stream.getAudioTracks?.()[0];
          if (videoTrack) pc.addTrack(videoTrack, stream);
          if (audioTrack) pc.addTrack(audioTrack, stream);
          pc.onicecandidate = (e) => {
            if (e.candidate && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: "ice", to: broadcasterId, payload: e.candidate.toJSON() })); } catch {}
            }
          };
          pc.oniceconnectionstatechange = () => {
            if (["connected", "completed"].includes(pc.iceConnectionState)) {
              if (!cancelled) setPublishState("publishing");
            } else if (["failed", "disconnected"].includes(pc.iceConnectionState)) {
              if (!cancelled) setPublishState("error");
            }
          };
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          try {
            ws.send(JSON.stringify({ type: "offer", to: broadcasterId, payload: { type: offer.type, sdp: offer.sdp } }));
          } catch { /* ws closed */ }
        };

        ws.onmessage = async (evt) => {
          let msg;
          try { msg = JSON.parse(evt.data); } catch { return; }
          if (msg.type === "welcome") {
            broadcasterId = msg.payload?.broadcaster_id;
            if (msg.payload?.ice_servers?.length) iceServers = msg.payload.ice_servers;
            if (broadcasterId) {
              // Ask the host for permission to publish — host's
              // LiveBroadcaster auto-accepts since the invite was sent
              // by them.
              try { ws.send(JSON.stringify({ type: "guest_publish_request", to: broadcasterId })); } catch {}
            }
          } else if (msg.type === "guest_publish_accept") {
            await sendOffer();
          } else if (msg.type === "answer" && publishPcRef.current) {
            try { await publishPcRef.current.setRemoteDescription(new RTCSessionDescription(msg.payload)); } catch (e) { console.warn(e); }
          } else if (msg.type === "ice" && publishPcRef.current) {
            try { await publishPcRef.current.addIceCandidate(msg.payload); } catch { /* race */ }
          } else if (msg.type === "host_online") {
            // Host reconnected after a drop — re-issue the publish ask
            // so the handshake restarts. Reset the offer guard so the
            // new accept frame produces a fresh PC.
            broadcasterId = msg.payload?.host_id || broadcasterId;
            sentOfferRef.current = false;
            try { publishPcRef.current?.close(); } catch {}
            publishPcRef.current = null;
            if (broadcasterId) {
              try { ws.send(JSON.stringify({ type: "guest_publish_request", to: broadcasterId })); } catch {}
            }
          }
        };
        ws.onerror = () => { if (!cancelled) setPublishState("error"); };
        ws.onclose  = () => { if (!cancelled) setPublishState("idle"); };
      } catch (e) {
        console.warn("Guest publish failed:", e);
        setPublishState("error");
      }
    })();

    return () => {
      cancelled = true;
      try { publishPcRef.current?.close(); } catch {}
      try { publishWsRef.current?.close(); } catch {}
      publishPcRef.current = null;
      publishWsRef.current = null;
      sentOfferRef.current = false;
      setPublishState("idle");
    };
    // compositedTrack is intentionally excluded — see the
    // replaceTrack effect below which hot-swaps the video sender
    // without tearing down the WebRTC handshake.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, stream, streamId]);

  // Hot-swap the published video track when the user toggles
  // green-screen mid-publish. RTCRtpSender.replaceTrack() lets us
  // change the outbound track without renegotiating, so the host
  // sees the new feed within ~one frame.
  useEffect(() => {
    const pc = publishPcRef.current;
    if (!pc) return;
    const videoSender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!videoSender) return;
    const next = compositedTrack || stream?.getVideoTracks?.()[0] || null;
    if (next && videoSender.track !== next) {
      try { videoSender.replaceTrack(next); } catch { /* renegotiation needed — extremely rare */ }
    }
  }, [compositedTrack, stream]);

  // Poll for host decision every 4s. When approved + on_air, hand off to
  // /live/{id}?guest=1 so the LiveBroadcaster's guest path picks up.
  useEffect(() => {
    if (!streamId || status === "error") return undefined;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}/guest/me`);
        // Pick up director-pushed message + countdown each poll. Compare
        // `id` so the same toast doesn't re-fire every 4s. Latest wins.
        if (data?.host_message_latest && data.host_message_latest.id !== hostMessage?.id) {
          setHostMessage(data.host_message_latest);
          toast(`${data.host_message_latest.from_name || "Director"}: ${data.host_message_latest.text}`, {
            id: `host-msg-${data.host_message_latest.id}`,
            duration: 6000,
          });
        }
        if (data?.countdown && data.countdown.ends_at) {
          setCountdown(data.countdown);
        } else if (countdown) {
          setCountdown(null);
          setCountdownLeft(0);
        }
        if (data?.status === "on_air") {
          // The guest stays here while on-air — the publish PC pumps
          // their cam to the host, who composites it into the outbound
          // broadcast. Viewers see the host's composite via the normal
          // /live/{id} path, so we do NOT redirect anywhere.
          if (status !== "on_air") {
            toast.success("You're on air!");
            setStatus("on_air");
          }
          return;
        }
        if (data?.status === "denied" || data?.status === "removed") {
          setStatus("denied");
          return;
        }
        if (data?.status === "approved") setStatus("approved");
        else if (data?.status === "requested") setStatus("requested");
      } catch { /* ignore — keep showing waiting */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, status]);

  // Countdown ticker — recompute remaining seconds 4x/sec while a
  // director-set countdown is active so the big timer in the UI stays
  // smooth. Auto-clears at 0.
  useEffect(() => {
    if (!countdown?.ends_at) { setCountdownLeft(0); return undefined; }
    const ends = (() => {
      try {
        const d = new Date(countdown.ends_at);
        return isNaN(d.getTime()) ? null : d.getTime();
      } catch { return null; }
    })();
    if (!ends) { setCountdownLeft(0); return undefined; }
    const tick = () => {
      const left = Math.max(0, Math.round((ends - Date.now()) / 1000));
      setCountdownLeft(left);
      if (left <= 0) setCountdown(null);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [countdown?.ends_at]);

  const headlineByStatus = {
    connecting: "Connecting you to the green room…",
    requested:  "Waiting for host to admit you",
    waiting:    "Waiting for host to admit you",
    approved:   "Host says you're up next",
    on_air:     "You're LIVE on the broadcast",
    denied:     "The host couldn't admit you this time",
    error:      "Something went wrong",
  };
  const subBycopy = {
    connecting: "Hang tight while we set up your camera and mic.",
    requested:  "The host can see your name in the Backstage panel. They'll admit you when they're ready.",
    waiting:    "The host can see your name in the Backstage panel. They'll admit you when they're ready.",
    approved: publishState === "publishing"
      ? "Connected — the host can see you. You'll be on-screen in a moment."
      : publishState === "connecting"
        ? "Connecting your camera to the host's broadcast…"
        : "Polishing the stage lights — you'll be live in a moment.",
    on_air: publishState === "publishing"
      ? "Your camera is streaming to the host. Stay on this page — closing it ends your appearance."
      : publishState === "error"
        ? "Camera link dropped — refresh to reconnect."
        : "Connecting your camera to the host…",
    denied:     "You can stay and watch as a regular viewer.",
    error:      error || "Try refreshing the page.",
  };

  return (
    <div data-testid="live-waiting-room" className="min-h-screen bg-gradient-to-br from-[#0A0F1A] via-[#0E1424] to-[#0A0F1A] text-white pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header strip */}
        <div className="flex items-center gap-3 mb-6">
          <button
            data-testid="waiting-room-back"
            onClick={() => nav(streamMeta ? `/live/${streamId}` : "/live")}
            className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Watch as viewer instead
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
          {/* Camera preview */}
          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden border border-white/10">
            <video
              ref={videoRef}
              data-testid="waiting-room-self-preview"
              className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""} ${camOn ? "" : "opacity-0"}`}
              playsInline muted autoPlay
            />
            {!camOn && (
              <div className="absolute inset-0 grid place-items-center text-white/60">
                <VideoOff className="w-12 h-12 mb-2 opacity-40" />
              </div>
            )}
            {/* Director countdown overlay — big number + label so the guest
                can finish their setup with a clear timer instead of "soon". */}
            {countdown && countdownLeft > 0 && (
              <div data-testid="waiting-room-countdown" className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className={`bg-black/70 backdrop-blur-sm border rounded-2xl px-8 py-6 text-center ${
                  countdown.auto_promote ? "border-tv-live/60 shadow-[0_0_40px_rgba(239,68,68,0.35)]" : "border-tv-accent/40"
                }`}>
                  <div className={`text-[10px] uppercase tracking-[0.3em] font-black ${
                    countdown.auto_promote ? "text-tv-live" : "text-tv-accent"
                  }`}>
                    {countdown.auto_promote ? "🎤 You're going LIVE in" : countdown.message || "You're up next"}
                  </div>
                  <div data-testid="waiting-room-countdown-secs" className="font-display font-black text-white text-7xl tracking-tighter mt-1">
                    {Math.floor(countdownLeft / 60) > 0
                      ? `${Math.floor(countdownLeft / 60)}:${String(countdownLeft % 60).padStart(2, "0")}`
                      : `${countdownLeft}s`}
                  </div>
                  <div className="text-white/60 text-[11px] mt-1">
                    {countdown.auto_promote ? "Auto-bringing you on-air at 0 — make sure your camera + mic are on." : `— from ${countdown.from_name || "Director"}`}
                  </div>
                </div>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 flex items-center justify-center gap-3">
              <button
                data-testid="waiting-room-toggle-mic"
                onClick={toggleMic}
                aria-pressed={!micOn}
                className={`p-3 rounded-full ${micOn ? "bg-white/15 hover:bg-white/25 text-white" : "bg-red-500 hover:bg-red-400 text-white"} transition`}
                title={micOn ? "Mute mic" : "Unmute mic"}
              >{micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</button>

              <button
                data-testid="waiting-room-toggle-cam"
                onClick={toggleCam}
                aria-pressed={!camOn}
                className={`p-3 rounded-full ${camOn ? "bg-white/15 hover:bg-white/25 text-white" : "bg-red-500 hover:bg-red-400 text-white"} transition`}
                title={camOn ? "Turn camera off" : "Turn camera on"}
              >{camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}</button>

              {hasMultipleCams && (
                <button
                  data-testid="waiting-room-flip-cam"
                  onClick={flipCamera}
                  className="p-3 rounded-full bg-white/15 hover:bg-white/25 text-white transition"
                  title="Flip camera (front / back)"
                ><RefreshCw className="w-5 h-5" /></button>
              )}
            </div>
            {/* Mic level bar */}
            <div className="absolute top-3 left-3 right-3 h-1 rounded-full bg-white/10 overflow-hidden" data-testid="waiting-room-mic-level">
              <div
                className="h-full bg-emerald-400 transition-[width] duration-75"
                style={{ width: `${micOn ? Math.round(level * 100) : 0}%` }}
              />
            </div>
            {/* Status badge */}
            <div className="absolute top-3 left-3">
              <span className="bg-black/70 border border-white/20 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-black">
                {status === "connecting" && (<><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Setting up</>)}
                {status === "requested" && (<><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> In waiting room</>)}
                {status === "approved" && (<><CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-400" /> Approved</>)}
                {status === "on_air" && (<><Radio className="w-3 h-3 inline mr-1 text-tv-live animate-pulse" /> On air!</>)}
                {status === "denied" && (<><AlertTriangle className="w-3 h-3 inline mr-1 text-amber-400" /> Not admitted</>)}
                {status === "error" && (<><AlertTriangle className="w-3 h-3 inline mr-1 text-red-400" /> Error</>)}
              </span>
            </div>
          </div>

          {/* Status side panel */}
          <aside data-testid="waiting-room-status" className="space-y-4">
            {streamMeta && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-tv-accent font-black mb-1">Tap In Live</div>
                <div className="font-display font-black text-lg text-white leading-tight">{streamMeta.title}</div>
                <div className="text-white/60 text-sm">Hosted by {streamMeta.creator_name || "the host"}</div>
              </div>
            )}
            {hostMessage && (
              <div data-testid="waiting-room-host-message" className="bg-tv-accent/15 border border-tv-accent/40 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-tv-accent font-black mb-1 inline-flex items-center gap-1.5">
                  <Radio className="w-3 h-3" /> {hostMessage.from_name || "Director"} says
                </div>
                <div className="font-display font-bold text-white text-base leading-snug">
                  "{hostMessage.text}"
                </div>
              </div>
            )}
            <div className={`rounded-xl p-4 border ${status === "denied" || status === "error" ? "bg-red-500/10 border-red-500/30" : "bg-tv-accent/5 border-tv-accent/30"}`}>
              <div className="text-[10px] uppercase tracking-widest font-black mb-1">{status === "denied" || status === "error" ? "Heads up" : "Status"}</div>
              <div className="font-display font-bold text-white leading-tight">{headlineByStatus[status]}</div>
              <div className="text-white/65 text-sm mt-1">{subBycopy[status]}</div>

              {status === "denied" && (
                <button
                  data-testid="waiting-room-watch-instead"
                  onClick={() => nav(`/live/${streamId}`)}
                  className="mt-3 w-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm py-2 rounded-full"
                >Watch as viewer →</button>
              )}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-[12px] text-white/70 leading-snug">
              <div className="text-[10px] uppercase tracking-widest text-white/45 font-black mb-1">Tips</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Find a well-lit spot; back-light kills picture quality.</li>
                <li>Use earbuds — open speakers cause echo on-air.</li>
                <li>Test your mic with the bar above before going live.</li>
                <li>Need a different camera? Tap the flip button (mobile).</li>
              </ul>
            </div>

            {/* iter-182: inline self-contained Green-Screen Studio.
                Owns its own pipeline, UI, and persistence. */}
            {canUseGreenScreen && (
              <GreenScreenStudio
                cameraStream={stream || null}
                onCompositedTrack={setCompositedTrack}
                collapsed={false}
                testidPrefix="waiting-room-gs"
              />
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
